/**
 * Footer Content Data
 *
 * Extracted from components/layout/footer.tsx for CMS readiness.
 * Contains brand info, navigation, credentials, and contact details.
 *
 * @see Story 3-6: Static Content Extraction for CMS
 */

export type NavLink = {
	label: string;
	href: string;
};

export type SocialLink = {
	platform: string;
	url: string;
	handle: string;
};

export type FooterContent = {
	brand: {
		name: string;
		tagline: string;
	};
	navigation: NavLink[];
	credentials: {
		license: string;
		education: string;
		socialLinks: SocialLink[];
	};
	contact: {
		phone: string;
		email: string;
		whatsapp?: string;
	};
	legal: {
		copyrightTemplate: string;
		links: NavLink[];
	};
};

/**
 * Footer content configuration
 */
export const FOOTER_CONTENT: FooterContent = {
	brand: {
		name: "Designs by Maryam",
		tagline: "Transforming spaces into extraordinary experiences",
	},
	navigation: [
		{ label: "Home", href: "/" },
		{ label: "About", href: "/about" },
		{ label: "Portfolio", href: "/portfolio" },
		{ label: "Contact", href: "/contact" },
	],
	credentials: {
		license: "CRPEP Licensed (Category B, October 2023)",
		education: "B.Sc. Interior Design, University of Bahrain",
		socialLinks: [
			{
				platform: "Instagram",
				url: "https://instagram.com/bymaryam.bh",
				handle: "@bymaryam.bh",
			},
		],
	},
	contact: {
		phone: "+973 3900 1234",
		email: "hello@designsbymaryam.com",
		whatsapp: "+97339001234",
	},
	legal: {
		// Use template for dynamic year
		copyrightTemplate: "© {year} Designs by Maryam. All rights reserved.",
		links: [
			{ label: "Privacy Policy", href: "/privacy" },
			{ label: "Terms of Service", href: "/terms" },
			{ label: "Client Portal", href: "/portal" },
			{ label: "Admin", href: "/admin" },
		],
	},
};

/**
 * Helper to get copyright text with current year
 */
export const getCopyrightText = (): string =>
	FOOTER_CONTENT.legal.copyrightTemplate.replace(
		"{year}",
		new Date().getFullYear().toString(),
	);

/**
 * Contact details also used by ContactInfo component
 */
export const CONTACT_DETAILS = {
	phone: FOOTER_CONTENT.contact.phone,
	email: FOOTER_CONTENT.contact.email,
	location: "Manama, Kingdom of Bahrain",
	businessHours: {
		weekdays: "Sunday - Thursday: 9:00 AM - 6:00 PM",
		weekend: "Friday - Saturday: By appointment",
	},
	responseTime: "We typically respond within 24 hours",
} as const;
